import { weenatIntl as wintl } from '@weenat/wintl';
import isNil from 'lodash-es/isNil';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { buildPredicateWithNewOperation, isOperationWithoutParams, predicateCreationSteps, removeParamSetupStep } from './alerts';
import navReducer from './navReducer';
const initialPredicateNavigationState = {
    steps: predicateCreationSteps,
    index: 0,
    history: []
};
const usePredicateEdition = ({ initialPredicate, disabledSources, onValidation }) => {
    const [currentPredicate, setCurrentPredicate] = useState(initialPredicate || {});
    const [navState, navDispatch] = useReducer(navReducer, initialPredicateNavigationState);
    // Other sources except the one we're trying to edit
    const unavailableSources = useMemo(() => disabledSources.filter((src) => src !== currentPredicate?.source), [currentPredicate, disabledSources]);
    const currentStepNumber = useMemo(() => navState.index + 1, [navState.index]);
    const currentStep = useMemo(() => navState.steps[navState.index], [navState.steps, navState.index]);
    const stepTitle = useMemo(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    () => wintl.t(`alerts.creationSteps.${currentStep}`, { capitalize: true }), [currentStep]);
    const handleValidation = (predicate) => {
        onValidation(predicate);
        setCurrentPredicate({});
    };
    const handleSourceSelection = useCallback((source) => {
        if (!isNil(source)) {
            if (currentPredicate?.source !== source) {
                setCurrentPredicate({ params: {}, source });
            }
            navDispatch({ type: 'goToNextStep' });
        }
    }, [currentPredicate, navDispatch, setCurrentPredicate]);
    const handleOperationSelection = useCallback((op) => {
        if (!isNil(op)) {
            let newSteps = predicateCreationSteps;
            const newPredicate = buildPredicateWithNewOperation(currentPredicate, op);
            // skipping param step depending on the type of operations
            if (isOperationWithoutParams(op)) {
                newSteps = removeParamSetupStep(navState.steps);
                setCurrentPredicate(newPredicate);
                handleValidation(newPredicate);
            }
            else {
                setCurrentPredicate(newPredicate);
            }
            navDispatch({ type: 'updateStepsAndGoToNext', newSteps });
        }
    }, [
        navState,
        navDispatch,
        currentPredicate,
        removeParamSetupStep,
        isOperationWithoutParams,
        buildPredicateWithNewOperation,
        handleValidation
    ]);
    const handleParamsSelection = useCallback((params) => {
        if (!isNil(params)) {
            const finalPredicate = {
                ...currentPredicate,
                params
            };
            setCurrentPredicate(finalPredicate);
            handleValidation(finalPredicate);
        }
    }, [currentPredicate, handleValidation]);
    const handlePreviousOnOperation = useCallback(() => {
        navDispatch({ type: 'goToPreviousStep' });
    }, [currentPredicate, navDispatch]);
    const handlePreviousOnParams = useCallback(() => {
        navDispatch({ type: 'goToPreviousStep' });
    }, [currentPredicate, navDispatch]);
    return {
        currentPredicate,
        currentStep,
        currentStepNumber,
        currentSteps: navState.steps,
        goToStep: (step) => navDispatch({ type: 'navTo', destination: step }),
        handleOperationSelection,
        handleParamsSelection,
        handlePreviousOnOperation,
        handlePreviousOnParams,
        handleSourceSelection,
        stepTitle,
        unavailableSources
    };
};
export default usePredicateEdition;
